import React from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Text,
    Box,
    Link,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useSiteContext} from '../../../../../contexts/site'
import usePdTagManager from '../../../../../hooks/use-pd-tag-manager'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

const Card = ({
    title,
    titleTag,
    titleColor,
    titleFontFamily,
    subheadText,
    subheadTextColor,
    subheadTextTag,
    subheadTextFontFamily,
    linkText,
    linkUrl,
    linkColor,
    linkAriaLabel,
    buttonText,
    buttonUrl,
    buttonBgColor,
    buttonTextColor,
    typeId,
    name,
    index
}) => {
    const appOrigin = useAppOrigin()
    const pdTagManager = usePdTagManager()
    const {isYR} = useSiteContext()
    const variant = isYR ? 'yr' : 'ks'
    const styles = useMultiStyleConfig('TextTilesX4', {variant})

    const isWhiteBg = (colorValue) => {
        const color = colorValue?.trim().toLowerCase()
        return color === 'white' || color === '#fff' || color === '#ffffff'
    }

    const getHoverMix = (bg) =>
        isWhiteBg(bg)
            ? 'color-mix(in srgb, white 90%, black 10%)'
            : `color-mix(in srgb, ${bg} 88%, white 12%)`

    return (
        <Stack
            height="100%"
            gap="10px"
            paddingTop="20px"
            paddingBottom={{base: '60px', md: '20px'}}
            paddingX={{base: '10px', md: '15px'}}
            textAlign="center"
        >
            {title && (
                <Text
                    as={titleTag}
                    color={titleColor}
                    fontFamily={titleFontFamily}
                    {...styles.title}
                >
                    {title}
                </Text>
            )}

            {subheadText && (
                <Text
                    as={subheadTextTag}
                    color={subheadTextColor}
                    fontFamily={subheadTextFontFamily}
                    {...styles.subheadText(linkText && linkUrl)}
                >
                    {subheadText}
                </Text>
            )}

            {linkText && linkUrl && (
                <Box>
                    <Link
                        href={linkUrl}
                        onClick={() => {
                            pdTagManager.selectComponent({
                                componentId: typeId,
                                componentName: name,
                                titleName: linkText,
                                titlePosition: index,
                                linkUrl:
                                    linkUrl?.indexOf('http') !== -1
                                        ? linkUrl
                                        : `${appOrigin}${linkUrl}`,
                                subText: subheadText,
                                tileHeading: title
                            })
                        }}
                        color={linkColor}
                        variant="underline"
                        fontWeight="300"
                        fontSize="17px"
                        lineHeight="26px"
                        letterSpacing="0.085px"
                        textUnderlineOffset="4px"
                        aria-label={linkAriaLabel || linkText}
                    >
                        {linkText}
                    </Link>
                </Box>
            )}

            {buttonText && buttonUrl && (
                <Box>
                    <Link
                        href={buttonUrl}
                        color={buttonTextColor}
                        variant="smallBtn"
                        backgroundColor={buttonBgColor}
                        transition="background-color .15s ease"
                        title={buttonText}
                        sx={{
                            _hover: {
                                backgroundColor: getHoverMix(buttonBgColor)
                            }
                        }}
                    >
                        {buttonText}
                    </Link>
                </Box>
            )}
        </Stack>
    )
}

Card.propTypes = {
    background: PropTypes.string,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,
    titleFontFamily: PropTypes.string,
    subheadText: PropTypes.string,
    subheadTextColor: PropTypes.string,
    subheadTextTag: PropTypes.string,
    subheadTextFontFamily: PropTypes.string,
    linkText: PropTypes.string,
    linkUrl: PropTypes.string,
    linkColor: PropTypes.string,
    linkAriaLabel: PropTypes.string,
    buttonText: PropTypes.string,
    buttonUrl: PropTypes.string,
    buttonBgColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.any,
    index: PropTypes.bool
}

export default Card
